import { useEffect, useRef, useState } from "react";
import type { NicheConfig } from "../../types/niche";
import type { PreviewResponse } from "../../types/preview";
import type { ReleasedDocument } from "../../types/api";
import type { ContractFormData } from "../../lib/contractSchema";
import { ApiError, generatePreview } from "../../lib/api";
import { evaluateDeadline } from "../../lib/deadline";
import { formatBRL, formatISODateBR } from "../../lib/format";
import { parseBRL } from "../../lib/masks";
import { buildLocalPreview } from "../../lib/mocks";
import { Stepper } from "./Stepper";
import { ContractForm } from "./ContractForm";
import type { FormReturnState } from "./ContractForm";
import { ProcessingScreen } from "./ProcessingScreen";
import { DocumentPreview } from "./DocumentPreview";

type FlowPhase = "form" | "processing" | "preview";

const STEPPER_INDEX: Record<FlowPhase, number> = { form: 0, processing: 1, preview: 1 };

const contractFacts = (data: ContractFormData) => [
  { k: "Data de assinatura", v: formatISODateBR(data.dataAssinatura) },
  { k: "Valor da entrada", v: formatBRL(parseBRL(data.valorEntrada)) },
  { k: "Forma de pagamento", v: data.formaPagamento },
];

function formReturnFor(err: ApiError, days: number): FormReturnState | null {
  switch (err.code) {
    case "DEADLINE_EXPIRED":
      return { step: 1, expiredDays: err.days ?? days, notice: null };
    case "DEADLINE_FUTURE":
      return { step: 1, expiredDays: null, notice: err.message };
    case "RATE_LIMITED":
    case "VALIDATION_ERROR":
      return { step: 2, expiredDays: null, notice: err.message };
    default:
      return null;
  }
}

export function MultipropriedadeFlow({ config }: { config: NicheConfig }) {
  const [phase, setPhase] = useState<FlowPhase>("form");
  const [data, setData] = useState<ContractFormData | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [formReturn, setFormReturn] = useState<FormReturnState | null>(null);
  const [released, setReleased] = useState<ReleasedDocument | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const prevPhase = useRef<FlowPhase>(phase);
  const requestId = useRef(0);

  useEffect(() => {
    const changed = prevPhase.current !== phase;
    prevPhase.current = phase;
    if (!changed || phase === "preview") return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    sectionRef.current?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  }, [phase]);

  const onSubmit = (values: ContractFormData, days: number) => {
    const id = ++requestId.current;
    setData(values);
    setPreview(null);
    setReleased(null);
    setFormReturn(null);
    setPhase("processing");
    generatePreview(values, days)
      .then((res) => {
        if (id === requestId.current) setPreview(res);
      })
      .catch((err: unknown) => {
        if (id !== requestId.current) return;
        const back = err instanceof ApiError ? formReturnFor(err, days) : null;
        if (back) {
          requestId.current += 1;
          setFormReturn(back);
          setPhase("form");
          return;
        }
        console.warn("Gepeto: falha ao gerar prévia no servidor; usando fallback local.", err instanceof ApiError ? err.code : err);
        setPreview(buildLocalPreview(values, days));
      });
  };

  const onRegenerate = () => {
    if (!data) return;
    const deadline = evaluateDeadline(data.dataAssinatura);
    if (deadline.status === "within") return onSubmit(data, deadline.days);
    setFormReturn({ step: 1, expiredDays: deadline.status === "expired" ? deadline.days : null, notice: null });
    setPhase("form");
  };

  const onEdit = () => {
    setFormReturn(null);
    setPhase("form");
  };

  return (
    <section ref={sectionRef} id="protocolo" data-testid="niche-flow" data-phase={released ? "released" : phase} className="scroll-mt-20 border-b border-slate-800">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
        <div className="rounded-md border border-slate-800 bg-slate-900">
          <div className="flex flex-col gap-6 border-b border-slate-800 p-6 sm:p-8">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-500">Protocolo de notificação</p>
            <Stepper current={released ? 2 : STEPPER_INDEX[phase]} />
          </div>
          {phase !== "preview" && (
            <div className="p-6 sm:p-8">
              {phase === "form" && <ContractForm config={config} defaultValues={data ?? undefined} returnState={formReturn} onSubmit={onSubmit} />}
              {phase === "processing" && <ProcessingScreen ready={preview !== null} onDone={() => setPhase("preview")} />}
            </div>
          )}
        </div>
        {phase === "preview" && data && preview && (
          <div className="mt-10">
            <DocumentPreview
              facts={contractFacts(data)}
              buyer={{ nomeCompleto: data.nomeCompleto.trim(), cpf: data.cpf, email: data.email.trim() }}
              preview={preview} released={released} price={config.price} onEdit={onEdit} onReleased={setReleased} onRegenerate={onRegenerate} />
          </div>
        )}
      </div>
    </section>
  );
}
